import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Search, SlidersHorizontal, Compass, X, ChevronLeft, ChevronRight, ChevronDown, BookOpen, Lightbulb, CheckCircle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { CareerBuddyModal, getPendingBuddyState, clearPendingBuddyState } from '@/components/CareerBuddyModal';

// --- Subject matching utilities ---

/** Parse a subject entry: "Mathematics/Applied Mathematics" → ["Mathematics", "Applied Mathematics"] */
function parseOrGroup(entry) {
  return entry.split('/').map(s => s.trim()).filter(Boolean);
}

/**
 * Count how many of the student's selected subjects are relevant to a career.
 * A selected subject counts if it appears in any OR group within required_subjects or helpful_subjects.
 */
function countSubjectMatches(career, selected) {
  const allGroups = [
    ...(career.recommended_subjects || career.required_subjects || []).map(parseOrGroup),
    ...(career.helpful_subjects || []).map(parseOrGroup),
  ];
  let matchCount = 0;
  for (const subj of selected) {
    if (allGroups.some(group => group.includes(subj))) {
      matchCount++;
    }
  }
  return matchCount;
}

/** Find required subject groups NOT satisfied by the student's selected subjects. */
function getUnsatisfiedRequired(career, selected) {
  return (career.recommended_subjects || career.required_subjects || [])
    .map(parseOrGroup)
    .filter(group => !group.some(alt => selected.includes(alt)));
}

// --- Components ---

function CareerCard({ career, onClick, selectedSubjects, matchScore }) {
  const cardImg = career.image_card_url || career.image_url;
  const total = selectedSubjects.length;
  const unsatisfied = total > 0 && matchScore >= 2 ? getUnsatisfiedRequired(career, selectedSubjects) : [];

  return (
    <div
      onClick={() => onClick(career)}
      className="group ce-card overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer"
      data-testid={`career-card-${career.slug}`}
    >
      <div className="aspect-[4/5] bg-gray-100 relative overflow-hidden rounded-t-2xl">
        {cardImg ? (
          <img
            src={cardImg}
            alt={career.name}
            className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
            <Compass className="h-10 w-10 text-gray-400" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
        <div className="absolute top-3 left-3">
          <span className="text-[11px] font-semibold bg-white/90 backdrop-blur-sm text-[#4B5563] px-2.5 py-1 rounded-lg">
            {career.category}
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-[#1F2937] font-['Outfit'] text-sm sm:text-base leading-tight mb-1.5 group-hover:text-[#F97D6A] transition-colors">
          {career.name}
        </h3>
        <p className="text-xs text-[#6B7280] line-clamp-2 leading-relaxed mb-3">
          {career.short_description}
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          {career.stream && (
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
              career.stream === 'Science' ? 'bg-sky-50 text-sky-700'
              : career.stream === 'Commerce' ? 'bg-amber-50 text-amber-700'
              : career.stream === 'Arts' ? 'bg-rose-50 text-rose-700'
              : career.stream === 'Any Stream' ? 'bg-violet-50 text-violet-700'
              : 'bg-gray-50 text-gray-600'
            }`}>
              {career.stream}
            </span>
          )}
          {total > 0 && matchScore >= 2 && (
            <span
              className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                matchScore === total ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
              }`}
              data-testid={`match-indicator-${career.slug}`}
            >
              {matchScore}/{total} subject match
            </span>
          )}
        </div>
        {unsatisfied.length > 0 && (
          <p className="text-[10px] text-amber-600 mt-1.5" data-testid={`also-requires-${career.slug}`}>
            Also requires: {unsatisfied.map(g => g.join(' or ')).join(', ')}
          </p>
        )}
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden animate-pulse">
      <div className="aspect-[4/5] bg-gray-100" />
      <div className="p-4">
        <div className="h-5 bg-gray-100 rounded w-3/4 mb-2" />
        <div className="h-3 bg-gray-100 rounded w-full mb-1" />
        <div className="h-3 bg-gray-100 rounded w-2/3 mb-3" />
        <div className="flex gap-2">
          <div className="h-5 bg-gray-100 rounded-full w-14" />
          <div className="h-5 bg-gray-100 rounded-full w-20" />
        </div>
      </div>
    </div>
  );
}

function ScoreSection({ score, total, items, onClick, selectedSubjects, defaultExpanded = 3 }) {
  const [showAll, setShowAll] = useState(false);
  if (!items || items.length === 0) return null;
  const visible = showAll ? items : items.slice(0, defaultExpanded);
  const remaining = items.length - defaultExpanded;
  const isFullMatch = score === total;

  return (
    <div className="mb-8" data-testid={`match-section-${score}-of-${total}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className={`text-sm font-bold font-['Outfit'] ${isFullMatch ? 'text-emerald-700' : 'text-[#1F2937]'}`}>
          {score}/{total} Matches
        </span>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${isFullMatch ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>{items.length}</span>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
        {visible.map(({ career, matchScore }) => (
          <CareerCard key={career.slug} career={career} onClick={onClick} selectedSubjects={selectedSubjects} matchScore={matchScore} />
        ))}
      </div>
      {!showAll && remaining > 0 && (
        <button
          onClick={() => setShowAll(true)}
          className="mt-3 text-sm font-medium text-[#F97D6A] hover:underline"
          data-testid={`see-all-${score}-of-${total}`}
        >
          See all {items.length} →
        </button>
      )}
    </div>
  );
}


function SuggestCareerDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({ requested_career_name: '', student_name: '', email: '', grade: '', school_name: '', why_interested: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/career-requests', form);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit suggestion');
    }
    setSubmitting(false);
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => { setSubmitted(false); setForm({ requested_career_name: '', student_name: '', email: '', grade: '', school_name: '', why_interested: '' }); }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="rounded-2xl max-w-md" data-testid="suggest-career-dialog">
        {submitted ? (
          <div className="text-center py-4" data-testid="suggest-career-success">
            <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-7 w-7 text-green-500" />
            </div>
            <h2 className="text-xl font-bold text-[#1F2937] font-['Outfit']">Thanks!</h2>
            <p className="text-sm text-[#4B5563] mt-2">We've received your suggestion. We'll try to add it to the platform.</p>
            <Button onClick={handleClose} className="mt-5 bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl" data-testid="suggest-done-btn">Done</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-['Outfit']">Suggest a Career</DialogTitle>
              <DialogDescription>Tell us which career you'd like to see on the platform.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3" data-testid="suggest-career-form">
              <div>
                <Label className="text-xs font-medium text-[#1F2937]">Career Name *</Label>
                <Input value={form.requested_career_name} onChange={(e) => setForm({ ...form, requested_career_name: e.target.value })} required placeholder="e.g. Marine Biologist" className="mt-1 rounded-xl h-10 border-gray-200 text-sm" data-testid="suggest-career-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-medium text-[#1F2937]">Your Name *</Label>
                  <Input value={form.student_name} onChange={(e) => setForm({ ...form, student_name: e.target.value })} required placeholder="Student or parent name" className="mt-1 rounded-xl h-10 border-gray-200 text-sm" data-testid="suggest-student-name" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-[#1F2937]">Email *</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder="you@email.com" className="mt-1 rounded-xl h-10 border-gray-200 text-sm" data-testid="suggest-email" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-medium text-[#6B7280]">Grade</Label>
                  <Input value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} placeholder="e.g. 10" className="mt-1 rounded-xl h-10 border-gray-200 text-sm" data-testid="suggest-grade" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-[#6B7280]">School Name</Label>
                  <Input value={form.school_name} onChange={(e) => setForm({ ...form, school_name: e.target.value })} placeholder="Your school" className="mt-1 rounded-xl h-10 border-gray-200 text-sm" data-testid="suggest-school" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium text-[#6B7280]">Why are you interested?</Label>
                <Textarea value={form.why_interested} onChange={(e) => setForm({ ...form, why_interested: e.target.value })} placeholder="Tell us briefly why this career interests you..." rows={2} className="mt-1 rounded-xl border-gray-200 text-sm resize-none" data-testid="suggest-why" />
              </div>
              <DialogFooter className="pt-1">
                <Button type="button" variant="outline" onClick={handleClose} className="rounded-xl">Cancel</Button>
                <Button type="submit" disabled={submitting} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl" data-testid="suggest-submit-btn">{submitting ? 'Submitting...' : 'Submit Suggestion'}</Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ExploreCareersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [careers, setCareers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('explore_filters') || '{}').search || ''; } catch { return ''; }
  });
  const [searchDebounced, setSearchDebounced] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('explore_filters') || '{}').search || ''; } catch { return ''; }
  });
  const [filters, setFilters] = useState({ categories: [], streams: [], subjects: [] });
  const [selectedCategory, setSelectedCategory] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('explore_filters') || '{}').category || ''; } catch { return ''; }
  });
  const [selectedStream, setSelectedStream] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('explore_filters') || '{}').stream || ''; } catch { return ''; }
  });
  const [selectedSubjects, setSelectedSubjects] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('explore_filters') || '{}').subjects || []; } catch { return []; }
  });
  const [page, setPage] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('explore_filters') || '{}').page || 1; } catch { return 1; }
  });
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(() => {
    try { return !!JSON.parse(sessionStorage.getItem('explore_filters') || '{}').showFilters; } catch { return false; }
  });
  const [subjectsOpen, setSubjectsOpen] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [matchOpen, setMatchOpen] = useState(false);
  const [matchedSlugs, setMatchedSlugs] = useState(null);
  const [matchResults, setMatchResults] = useState([]);
  const [buddyRestore, setBuddyRestore] = useState(false);

  // Persist filter state to sessionStorage on change
  useEffect(() => {
    const state = {
      search, category: selectedCategory, stream: selectedStream,
      subjects: selectedSubjects, page,
      showFilters: showFilters || selectedCategory || selectedStream || selectedSubjects.length > 0,
    };
    sessionStorage.setItem('explore_filters', JSON.stringify(state));
  }, [search, selectedCategory, selectedStream, selectedSubjects, page, showFilters]);

  // Check for Career Buddy restore after login redirect
  useEffect(() => {
    if (searchParams.get('buddyRestore') === '1') {
      const pending = getPendingBuddyState();
      if (pending) {
        setBuddyRestore(true);
        setMatchOpen(true);
      }
      // Clean URL
      searchParams.delete('buddyRestore');
      setSearchParams(searchParams, { replace: true });
    }
    // Handle openBuddy param from dashboard CTA
    if (searchParams.get('openBuddy') === '1') {
      setMatchOpen(true);
      searchParams.delete('openBuddy');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    api.get('/careers/filters').then((res) => setFilters(res.data)).catch(() => {});
  }, []);

  const fetchCareers = useCallback(async () => {
    setLoading(true);
    try {
      // When match mode is active, fetch all and filter client-side
      if (matchedSlugs && matchedSlugs.length > 0) {
        const res = await api.get('/careers?limit=100');
        const all = res.data.careers || [];
        const filtered = matchedSlugs
          .map(slug => all.find(c => c.slug === slug))
          .filter(Boolean);
        setCareers(filtered);
        setTotalPages(1);
        setTotal(filtered.length);
        setLoading(false);
        return;
      }
      const params = new URLSearchParams();
      if (searchDebounced) params.append('search', searchDebounced);
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedStream) params.append('stream', selectedStream);
      // When subjects selected, fetch all for client-side grouping
      if (selectedSubjects.length > 0) {
        params.append('limit', '200');
      } else {
        params.append('page', page);
        params.append('limit', '18');
      }
      const res = await api.get(`/careers?${params.toString()}`);
      setCareers(res.data.careers);
      setTotalPages(selectedSubjects.length > 0 ? 1 : res.data.pages);
      setTotal(res.data.total);
    } catch {
      toast.error('Failed to load careers');
    }
    setLoading(false);
  }, [searchDebounced, selectedCategory, selectedStream, selectedSubjects, page, matchedSlugs]);

  useEffect(() => {
    fetchCareers();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [fetchCareers]);

  useEffect(() => {
    setPage(1);
  }, [searchDebounced, selectedCategory, selectedStream, selectedSubjects, matchedSlugs]);

  const handleCareerClick = (career) => {
    navigate(`/careers/${career.slug}`);
  };

  const clearFilters = () => {
    setSearch('');
    setSelectedCategory('');
    setSelectedStream('');
    setSelectedSubjects([]);
    setMatchedSlugs(null);
    setMatchResults([]);
    sessionStorage.removeItem('explore_filters');
  };

  const clearMatch = () => {
    setMatchedSlugs(null);
    setMatchResults([]);
  };

  const handleMatchComplete = (slugs, results, navigateSlug) => {
    // Clear other filters so match results are clean
    setSearch('');
    setSelectedCategory('');
    setSelectedStream('');
    setSelectedSubjects([]);
    setMatchedSlugs(slugs);
    setMatchResults(results || []);
    setPage(1);
    if (navigateSlug) {
      navigate(`/careers/${navigateSlug}`);
    }
  };

  // Mutually exclusive subject pairs
  const EXCLUSIVE_PAIRS = {
    'Mathematics': 'Applied Mathematics',
    'Applied Mathematics': 'Mathematics',
    'Computer Science': 'Artificial Intelligence',
    'Artificial Intelligence': 'Computer Science',
  };

  const toggleSubject = (subject) => {
    setSelectedSubjects((prev) => {
      if (prev.includes(subject)) {
        return prev.filter((s) => s !== subject);
      }
      // Remove mutually exclusive subject if present
      const exclusive = EXCLUSIVE_PAIRS[subject];
      const filtered = exclusive ? prev.filter((s) => s !== exclusive) : prev;
      return [...filtered, subject];
    });
  };

  const isSubjectDisabled = (subject) => {
    const exclusive = EXCLUSIVE_PAIRS[subject];
    return exclusive ? selectedSubjects.includes(exclusive) : false;
  };

  const hasActiveFilters = search || selectedCategory || selectedStream || selectedSubjects.length > 0 || matchedSlugs;

  return (
    <div className="py-6 sm:py-8" data-testid="explore-careers-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-[#FFF0ED] text-[#F97D6A] px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            <Compass className="h-4 w-4" />
            Career Discovery
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1F2937] font-['Outfit'] mb-3">
            Explore Careers
          </h1>
          <p className="text-sm sm:text-base text-[#6B7280] max-w-2xl mx-auto">
            Discover where your subjects can take you. Explore careers, understand your options, and find your path.
          </p>
        </div>

        {/* Career Buddy CTA */}
        {!matchedSlugs && (
          <div className="mb-6 bg-gradient-to-r from-[#FFF7F5] to-[#FFF0ED] border border-[#F97D6A]/15 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row items-center justify-between gap-3" data-testid="career-buddy-cta">
            <div className="flex items-center gap-3 text-center sm:text-left">
              <div className="w-10 h-10 rounded-xl bg-white/80 flex items-center justify-center shrink-0 shadow-sm">
                <Sparkles className="h-5 w-5 text-[#F97D6A]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-[#1F2937] font-['Outfit']">Not sure where to start?</p>
                <p className="text-xs text-[#6B7280]">Answer 6 quick questions and let Career Buddy guide you to careers that fit you</p>
              </div>
            </div>
            <Button
              onClick={() => setMatchOpen(true)}
              className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-10 px-6 text-sm font-semibold gap-2 shrink-0 shadow-sm shadow-[#F97D6A]/20"
              data-testid="try-career-buddy-btn"
            >
              <Sparkles className="h-4 w-4" />
              Try Career Buddy
            </Button>
          </div>
        )}

        {/* Match Active Banner */}
        {matchedSlugs && (
          <div className="mb-6 bg-emerald-50 border border-emerald-200/60 rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-3" data-testid="match-active-banner">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                <Sparkles className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-emerald-800 font-['Outfit']">
                  Showing {matchedSlugs.length} careers matched for you
                </p>
                <p className="text-xs text-emerald-600">Based on your Career Buddy answers</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setMatchOpen(true)}
                className="rounded-xl h-9 px-4 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                data-testid="retake-match-btn"
              >
                <Sparkles className="h-3.5 w-3.5 mr-1" />
                Retake
              </Button>
              <Button
                variant="outline"
                onClick={clearMatch}
                className="rounded-xl h-9 px-4 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                data-testid="reset-match-filter-btn"
              >
                <X className="h-3.5 w-3.5 mr-1" />
                Reset Match
              </Button>
            </div>
          </div>
        )}

        {/* Search & Filter Bar */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5 mb-6 shadow-sm">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search careers by name, subject, or keyword..."
                className="pl-10 h-11 rounded-xl border-gray-200 text-sm"
                data-testid="career-search-input"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`h-11 rounded-xl px-4 gap-2 shrink-0 ${showFilters ? 'bg-[#FFF0ED] text-[#F97D6A] border-[#F97D6A]' : ''}`}
              data-testid="toggle-filters-btn"
            >
              <SlidersHorizontal className="h-4 w-4" />
              Filters
              {hasActiveFilters && <span className="w-2 h-2 rounded-full bg-[#F97D6A]" />}
            </Button>
          </div>

          {showFilters && (<>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4 pt-4 border-t border-gray-100" data-testid="filters-section">
              <Select
                value={selectedCategory || 'all'}
                onValueChange={(v) => setSelectedCategory(v === 'all' ? '' : v)}
              >
                <SelectTrigger className="h-10 rounded-xl border-gray-200 text-sm" data-testid="filter-category">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {filters.categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={selectedStream || 'all'}
                onValueChange={(v) => setSelectedStream(v === 'all' ? '' : v)}
              >
                <SelectTrigger className="h-10 rounded-xl border-gray-200 text-sm" data-testid="filter-stream">
                  <SelectValue placeholder="All Streams" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Streams</SelectItem>
                  {filters.streams.map((s) => (
                    <SelectItem key={s} value={s}>
                      <span className="flex items-center gap-1.5">
                        <span className={`w-2 h-2 rounded-full ${
                          s === 'Science' ? 'bg-sky-500' : s === 'Commerce' ? 'bg-amber-500' : s === 'Any Stream' ? 'bg-violet-500' : 'bg-rose-500'
                        }`} />
                        {s}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Popover open={subjectsOpen} onOpenChange={setSubjectsOpen}>
                <PopoverTrigger asChild>
                  <button
                    className="flex items-center justify-between h-10 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-left hover:bg-gray-50 transition-colors"
                    data-testid="filter-subjects"
                  >
                    <span className={selectedSubjects.length > 0 ? 'text-[#1F2937]' : 'text-[#9CA3AF]'}>
                      {selectedSubjects.length > 0
                        ? `${selectedSubjects.length} subject${selectedSubjects.length > 1 ? 's' : ''} selected`
                        : 'Select Subjects'}
                    </span>
                    <ChevronDown className="h-4 w-4 text-[#9CA3AF] shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[260px] p-0" align="start">
                  <div className="p-2 border-b border-gray-100">
                    <p className="text-xs font-semibold text-[#6B7280] px-1">Select subjects</p>
                  </div>
                  <div className="max-h-[240px] overflow-y-auto p-1.5">
                    {filters.subjects.map((subject) => {
                      const disabled = isSubjectDisabled(subject);
                      return (
                      <label
                        key={subject}
                        className={`flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-sm ${disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'}`}
                      >
                        <Checkbox
                          checked={selectedSubjects.includes(subject)}
                          onCheckedChange={() => !disabled && toggleSubject(subject)}
                          disabled={disabled}
                          className="h-4 w-4"
                        />
                        <span className="text-[#1F2937]">{subject}</span>
                      </label>
                      );
                    })}
                  </div>
                  {selectedSubjects.length > 0 && (
                    <div className="p-2 border-t border-gray-100">
                      <button
                        onClick={() => setSelectedSubjects([])}
                        className="text-xs text-[#F97D6A] hover:underline w-full text-center"
                      >
                        Clear subjects
                      </button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            </div>
            <p className="text-[11px] text-[#9CA3AF] mt-1.5 px-0.5">English is not included here as it is part of your compulsory school subjects. Select the elective subjects you are considering.</p>
          </>)}

          {hasActiveFilters && (
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <span className="text-xs text-[#9CA3AF]">Active:</span>
              {selectedCategory && (
                <Badge className="text-[11px] bg-[#F5F6F8] text-[#4B5563] border-0 rounded-full gap-1 cursor-pointer hover:bg-gray-200" onClick={() => setSelectedCategory('')}>
                  {selectedCategory} <X className="h-3 w-3" />
                </Badge>
              )}
              {selectedStream && (
                <Badge className={`text-[11px] border-0 rounded-full gap-1 cursor-pointer ${
                  selectedStream === 'Science' ? 'bg-sky-50 text-sky-700 hover:bg-sky-100'
                  : selectedStream === 'Commerce' ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                  : selectedStream === 'Any Stream' ? 'bg-violet-50 text-violet-700 hover:bg-violet-100'
                  : 'bg-rose-50 text-rose-700 hover:bg-rose-100'
                }`} onClick={() => setSelectedStream('')}>
                  {selectedStream} <X className="h-3 w-3" />
                </Badge>
              )}
              {selectedSubjects.map((sub) => (
                <Badge key={sub} className="text-[11px] bg-emerald-50 text-emerald-700 border-0 rounded-full gap-1 cursor-pointer hover:bg-emerald-100" onClick={() => toggleSubject(sub)}>
                  {sub} <X className="h-3 w-3" />
                </Badge>
              ))}
              <button onClick={clearFilters} className="text-xs text-[#F97D6A] hover:underline ml-1">
                Clear all
              </button>
            </div>
          )}
        </div>

        {!loading && (
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-[#6B7280]" data-testid="results-count">
              {total > 0 ? `Showing ${careers.length} of ${total} careers` : 'No careers found'}
            </p>
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : careers.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center" data-testid="no-careers-found">
            <div className="w-16 h-16 rounded-2xl bg-[#F5F6F8] flex items-center justify-center mx-auto mb-4">
              <Search className="h-8 w-8 text-[#D1D5DB]" />
            </div>
            <h3 className="text-base font-semibold text-[#1F2937] font-['Outfit'] mb-1">
              No careers found
            </h3>
            <p className="text-sm text-[#9CA3AF] mb-4 max-w-sm mx-auto">
              Try adjusting your search or filters to discover more career options.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              {hasActiveFilters && (
                <Button onClick={clearFilters} variant="outline" className="rounded-xl text-sm" data-testid="clear-filters-btn">
                  Clear all filters
                </Button>
              )}
              <Button onClick={() => setSuggestOpen(true)} variant="outline" className="rounded-xl text-sm gap-1.5 border-[#F97D6A]/30 text-[#F97D6A] hover:bg-[#FFF0ED]" data-testid="suggest-from-empty-btn">
                <Lightbulb className="h-3.5 w-3.5" />
                Suggest a career
              </Button>
            </div>
          </div>
        ) : selectedSubjects.length > 0 && !searchDebounced ? (
          // N/N score-grouped view
          (() => {
            const total = selectedSubjects.length;
            const scored = careers.map(career => ({
              career,
              matchScore: countSubjectMatches(career, selectedSubjects),
            }));
            // Group by score, descending, only show score >= 2
            const groups = {};
            for (const item of scored) {
              if (item.matchScore < 2) continue;
              if (!groups[item.matchScore]) groups[item.matchScore] = [];
              groups[item.matchScore].push(item);
            }
            // Sort each group alphabetically
            for (const key of Object.keys(groups)) {
              groups[key].sort((a, b) => a.career.name.localeCompare(b.career.name));
            }
            const scores = Object.keys(groups).map(Number).sort((a, b) => b - a);
            const totalShown = scores.reduce((sum, s) => sum + groups[s].length, 0);
            return (
              <div data-testid="grouped-results">
                {scores.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
                    <p className="text-sm text-[#9CA3AF]">No careers match 2 or more of your selected subjects. Try selecting different subjects.</p>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-[#6B7280] mb-4">{totalShown} career{totalShown !== 1 ? 's' : ''} matching 2 or more of your {total} selected subjects</p>
                    {scores.map((score, idx) => (
                      <ScoreSection key={score} score={score} total={total} items={groups[score]} onClick={handleCareerClick} selectedSubjects={selectedSubjects} defaultExpanded={idx === 0 ? 9 : 3} />
                    ))}
                  </>
                )}
              </div>
            );
          })()
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="careers-grid">
            {careers.map((career) => (
              <CareerCard key={career.slug} career={career} onClick={handleCareerClick} selectedSubjects={selectedSubjects} matchScore={selectedSubjects.length > 0 ? countSubjectMatches(career, selectedSubjects) : undefined} />
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-8" data-testid="pagination">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-xl h-9 px-4"
              data-testid="prev-page-btn"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <span className="text-sm text-[#6B7280]">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="rounded-xl h-9 px-4"
              data-testid="next-page-btn"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}

        {/* Suggest a Career CTA */}
        {!loading && (
          <div className="mt-10 text-center" data-testid="suggest-career-section">
            <p className="text-sm font-semibold text-[#1F2937] font-['Outfit'] mb-1">Can't find a career?</p>
            <p className="text-xs text-[#9CA3AF] mb-3">Suggest a career and we'll try to add it to the platform.</p>
            <Button
              onClick={() => setSuggestOpen(true)}
              variant="outline"
              className="rounded-xl text-sm gap-1.5 border-[#F97D6A]/30 text-[#F97D6A] hover:bg-[#FFF0ED] h-9 px-5"
              data-testid="suggest-career-btn"
            >
              <Lightbulb className="h-3.5 w-3.5" />
              Suggest one. We will add it.
            </Button>
          </div>
        )}

        <SuggestCareerDialog open={suggestOpen} onOpenChange={setSuggestOpen} />
        <CareerBuddyModal
          open={matchOpen}
          onOpenChange={(v) => { setMatchOpen(v); if (!v) setBuddyRestore(false); }}
          onMatchComplete={handleMatchComplete}
          restoreMode={buddyRestore}
        />
      </div>
    </div>
  );
}
